import React, { useEffect, useState } from "react";
import { fetchJson, postJson } from "../../services/api";
import { APILog, ActionQueueResponse } from "../types";
import StatusBadge from "../components/StatusBadge";
import { PageHeader, SectionCard, Loader, ErrorMsg, refreshBtnStyle } from "../components/Common";

type ActionModal = {
  log: APILog;
  actionStatus: string;
  actionBy: string;
  actionMessage: string;
  priority: string;
} | null;

const ActionQueuePage: React.FC = () => {
  const [logs, setLogs]         = useState<APILog[]>([]);
  const [total, setTotal]       = useState(0);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState<string | null>(null);
  const [modal, setModal]       = useState<ActionModal>(null);
  const [saving, setSaving]     = useState(false);
  const [counts, setCounts]     = useState<Record<string, number>>({ "": 0, Pending: 0, Resolved: 0, Ignored: 0 });
  const [filterStatus, setFilterStatus] = useState("Pending");
  const [timeZone, setTimeZone] = useState<"UTC" | "IST">("UTC");

  const fetchCounts = async () => {
    try {
      const statuses = ["", "Pending", "Resolved", "Ignored"];
      const results = await Promise.all(
        statuses.map((s) => {
          const params: Record<string, string | number> = { limit: 1 };
          if (s) params.action_status = s;
          return fetchJson<ActionQueueResponse>("/monitor/actions/", params);
        })
      );
      const newCounts: Record<string, number> = {};
      results.forEach((r, i) => { newCounts[statuses[i]] = r.total || 0; });
      setCounts(newCounts);
    } catch {
      // ignore count failures
    }
  };

  const load = () => {
    setLoading(true);
    const params: Record<string, string | number> = { limit: 100 };
    if (filterStatus) params.action_status = filterStatus;
    fetchJson<ActionQueueResponse>("/monitor/actions/", params)
      .then((res) => { setLogs(res.logs); setTotal(res.total); setError(null); })
      .catch((e) => setError(e.message))
      .finally(() => { setLoading(false); fetchCounts(); });
  };

  useEffect(() => { load(); }, [filterStatus]); // eslint-disable-line

  const allCount = counts.Pending + counts.Resolved + counts.Ignored;

  const openModal = (log: APILog) =>
    setModal({ log, actionStatus: "Resolved", actionBy: "", actionMessage: "", priority: log.priority || "Medium" });

  const submitAction = async () => {
    if (!modal) return;
    if (!modal.actionBy.trim()) { alert("Please enter your name in Action By."); return; }
    setSaving(true);
    try {
      await postJson("/monitor/actions/update/", {
        id:             modal.log._id,
        action_status:  modal.actionStatus,
        action_by:      modal.actionBy,
        action_message: modal.actionMessage,
        priority:       modal.priority,
      });
      setModal(null);
      load();
    } catch (e: any) {
      alert("Failed to update: " + e.message);
    } finally {
      setSaving(false);
    }
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    if (timeZone === "IST") {
      return new Date(date.getTime() + 5.5 * 3600_000).toLocaleString("en-IN", { hour12: true });
    }
    return date.toLocaleString("en-US", { hour12: true }) + " UTC";
  };

  if (loading) return <Loader />;
  if (error)   return <ErrorMsg msg={error} onRetry={load} />;

  return (
    <div>
      <PageHeader title="Action Queue" subtitle="Review and resolve failed API calls" />

      {/* Tabs + timezone */}
      <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
        {(["Pending", "Resolved", "Ignored", ""] as const).map((s) => (
          <button
            key={s}
            onClick={() => setFilterStatus(s)}
            style={{
              ...refreshBtnStyle,
              background: filterStatus === s ? "var(--info)" : "var(--surface)",
              color: filterStatus === s ? "#fff" : "var(--text)",
              border: "1px solid var(--border)",
            }}
          >
            {s || "All"} ({s === "" ? allCount : counts[s] ?? 0})
          </button>
        ))}

        <div style={{ display: "flex", alignItems: "center", gap: 6, marginLeft: "auto" }}>
          <span style={{ fontSize: 12, color: "var(--muted)" }}>Time Zone:</span>
          <select
            value={timeZone}
            onChange={(e) => setTimeZone(e.target.value as "UTC" | "IST")}
            style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 6, padding: "6px 10px", color: "var(--text)", fontSize: 12, outline: "none" }}
          >
            <option value="UTC">UTC</option>
            <option value="IST">India (UTC +5:30)</option>
          </select>
          <button onClick={load} style={refreshBtnStyle}>↻ Refresh</button>
        </div>
      </div>

      <SectionCard title={`${filterStatus || "All"} — ${total} items`}>
        <div style={{ overflowX: "auto", overflowY: "auto", maxHeight: 680 }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead>
              <tr style={{ background: "var(--surface-soft)" }}>
                {["DateTime", "School", "Login", "Endpoint", "Code", "Error Type", "Priority", "Action Status", "Action By", ""].map((h) => (
                  <th key={h} style={{ padding: "10px 12px", color: "var(--muted)", textAlign: "left", fontSize: 11, fontWeight: 600, whiteSpace: "nowrap" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {logs.length === 0
                ? <tr><td colSpan={10} style={{ padding: 32, textAlign: "center", color: "var(--muted)" }}>No items found</td></tr>
                : logs.map((log) => (
                  <tr key={log._id} style={{ borderBottom: "1px solid var(--border)" }}>
                    <td style={td}>{formatDateTime(log.datetime)}</td>
                    <td style={td}>{log.school_code || "—"}</td>
                    <td style={td}>{log.login       || "—"}</td>
                    <td style={{ ...td, fontFamily: "monospace", maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{log.endpoint}</td>
                    <td style={td}><StatusBadge value={log.status_code} type="status_code" /></td>
                    <td style={td}>{log.error_type   || "—"}</td>
                    <td style={td}>{log.priority     ? <StatusBadge value={log.priority}      type="priority"      /> : "—"}</td>
                    <td style={td}>{log.action_status ? <StatusBadge value={log.action_status} type="action_status" /> : "—"}</td>
                    <td style={td}>{log.action_by    || "—"}</td>
                    <td style={td}>
                      <button
                        onClick={() => openModal(log)}
                        style={{ ...refreshBtnStyle, background: "var(--surface-soft)", color: "var(--text)", border: "1px solid var(--border)" }}
                      >
                        Update
                      </button>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </SectionCard>

      {/* Update Modal */}
      {modal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.75)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
          <div style={{ background: "var(--surface)", borderRadius: 12, padding: 28, width: 480, maxWidth: "90vw", border: "1px solid var(--border)" }}>
            <h3 style={{ color: "var(--text)", marginBottom: 6, fontSize: 16, marginTop: 0 }}>Update Action</h3>
            <p style={{ color: "var(--muted)", fontSize: 12, marginBottom: 20, fontFamily: "monospace" }}>{modal.log.endpoint}</p>

            <ModalField label="Action Status">
              <select value={modal.actionStatus} onChange={(e) => setModal({ ...modal, actionStatus: e.target.value })} style={selectStyle}>
                <option value="Resolved">Resolved</option>
                <option value="Ignored">Ignored</option>
                <option value="Pending">Pending</option>
              </select>
            </ModalField>

            <ModalField label="Priority">
              <select value={modal.priority} onChange={(e) => setModal({ ...modal, priority: e.target.value })} style={selectStyle}>
                <option value="High">High</option>
                <option value="Medium">Medium</option>
                <option value="Low">Low</option>
              </select>
            </ModalField>

            <ModalField label="Action By *">
              <input
                value={modal.actionBy}
                onChange={(e) => setModal({ ...modal, actionBy: e.target.value })}
                placeholder="Your name"
                style={inputStyle}
              />
            </ModalField>

            <ModalField label="Action Message">
              <textarea
                value={modal.actionMessage}
                onChange={(e) => setModal({ ...modal, actionMessage: e.target.value })}
                placeholder="What was done to fix this?"
                rows={3}
                style={{ ...inputStyle, resize: "vertical" }}
              />
            </ModalField>

            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 20 }}>
              <button onClick={() => setModal(null)} style={{ ...refreshBtnStyle, background: "var(--surface)", color: "var(--text)", border: "1px solid var(--border)" }}>
                Cancel
              </button>
              <button onClick={submitAction} disabled={saving} style={{ ...refreshBtnStyle, background: "var(--success)", color: "#fff", opacity: saving ? 0.6 : 1 }}>
                {saving ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ActionQueuePage;

const td: React.CSSProperties = { padding: "9px 12px", color: "var(--muted)", whiteSpace: "nowrap" };
const selectStyle: React.CSSProperties = { width: "100%", background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 6, padding: "7px 10px", color: "var(--text)", fontSize: 13, outline: "none" };
const inputStyle: React.CSSProperties  = { width: "100%", background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 6, padding: "7px 10px", color: "var(--text)", fontSize: 13, outline: "none", boxSizing: "border-box" };

const ModalField: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div style={{ marginBottom: 14 }}>
    <div style={{ fontSize: 11, color: "var(--muted)", marginBottom: 5 }}>{label}</div>
    {children}
  </div>
);
